import {
	canAccessInternalEvents,
	canAccessRoasting,
	canAccessMarketingEmails,
	canViewAccountingBills,
	hasPermission,
} from "@/lib/roles";
import { m } from "@/paraglide/messages.js";

export interface NavItem {
	path: string;
	label: string;
	children?: NavItem[];
	isVisible?: (userRole?: string, userEmail?: string | null) => boolean;
}

export type NavGroupId =
	| "roasting"
	| "inventory"
	| "sales"
	| "marketing"
	| "logistics"
	| "support"
	| "accounting"
	| "admin";

export interface NavGroup {
	id: NavGroupId;
	label: string;
	basePath: string;
	items: NavItem[];
}

interface NavGroupDefinition extends NavGroup {
	isVisible: (userRole?: string, userEmail?: string | null) => boolean;
}

function getInventoryItems(): NavItem[] {
	return [
		{ path: "/inventory", label: m.nav_dashboard() },
		{ path: "/inventory/products", label: m.nav_products() },
		{ path: "/inventory/cargos", label: m.nav_cargos() },
		{
			path: "/inventory/samples",
			label: m.nav_samples(),
			isVisible: (userRole) => hasPermission(userRole, "samples:view"),
		},
		{ path: "/inventory/reservations", label: m.nav_reservations() },
		{ path: "/inventory/audit", label: m.nav_audit() },
	];
}

function getSalesItems(): NavItem[] {
	return [
		{ path: "/sales", label: m.nav_dashboard() },
		{
			path: "/sales/crm",
			label: "CRM",
			isVisible: (userRole) => hasPermission(userRole, "sales:view"),
			children: [
				{
					path: "/sales/crm/opportunities",
					label: "Opportunities",
					isVisible: (userRole) => hasPermission(userRole, "sales:view"),
				},
				{
					path: "/sales/crm/leads",
					label: "Leads",
					isVisible: (userRole) => userRole?.toLowerCase() === "sales",
				},
				{
					path: "/sales/crm/follow-ups",
					label: "Follow-ups",
					isVisible: (userRole) => hasPermission(userRole, "sales:view"),
				},
				{
					path: "/sales/crm/people",
					label: "People",
					isVisible: (userRole) => hasPermission(userRole, "sales:view"),
				},
			],
		},
		{
			path: "/sales/analytics",
			label: m.nav_analytics(),
			isVisible: (userRole) => hasPermission(userRole, "sales:view"),
		},
		{
			path: "/sales/orders",
			label: m.nav_orders(),
			isVisible: (userRole) => hasPermission(userRole, "orders:view"),
		},
		{
			path: "/sales/contracts",
			label: m.nav_contracts(),
			isVisible: (userRole) => hasPermission(userRole, "contracts:view"),
		},
		{
			path: "/sales/samples",
			label: m.nav_samples(),
			isVisible: (userRole) => hasPermission(userRole, "samples:view"),
		},
	];
}

function canViewLogisticsNav(userRole?: string): boolean {
	return (
		hasPermission(userRole, "logistics:view") ||
		hasPermission(userRole, "inventory:view")
	);
}

function canViewLogisticsUsersNav(userRole?: string): boolean {
	return (
		hasPermission(userRole, "logistics:edit") ||
		hasPermission(userRole, "inventory:edit")
	);
}

function getLogisticsItems(): NavItem[] {
	return [
		{ path: "/logistics", label: m.nav_dashboard() },
		{ path: "/logistics/orders", label: m.nav_orders() },
		{
			path: "/logistics/inbound-deliveries",
			label: "Inbound Deliveries",
			isVisible: (userRole) => hasPermission(userRole, "logistics:edit"),
		},
		{
			path: "/logistics/international-purchases",
			label: "International Purchases",
			isVisible: (userRole) => hasPermission(userRole, "logistics:edit"),
		},
		{
			path: "/logistics/outbound-shipments",
			label: "Outbound Shipments",
			isVisible: (userRole) => hasPermission(userRole, "logistics:edit"),
		},
		{
			path: "/logistics/shipping-adjustments",
			label: m.nav_shipping_adjustments(),
			isVisible: (userRole) => hasPermission(userRole, "logistics:edit"),
		},
		{
			path: "/logistics/shipping-quotes",
			label: "Shipping Quotes",
			isVisible: (userRole) => hasPermission(userRole, "logistics:edit"),
		},
		{
			path: "/logistics/carrier-notices",
			label: m.nav_carrier_notices(),
			isVisible: (userRole) => hasPermission(userRole, "logistics:edit"),
		},
		{
			path: "/logistics/users",
			label: m.nav_user_settings(),
			isVisible: canViewLogisticsUsersNav,
		},
	];
}

function getSupportItems(): NavItem[] {
	return [
		{ path: "/support", label: m.nav_inbox() },
		{ path: "/security", label: m.nav_security() },
		{
			path: "/support/agents",
			label: m.nav_ai_agents(),
			isVisible: (userRole) => hasPermission(userRole, "admin:view"),
		},
	];
}

function getAccountingItems(): NavItem[] {
	return [
		{
			path: "/admin/accounting",
			label: m.nav_financial_settings(),
			isVisible: (userRole) => hasPermission(userRole, "accounting:view"),
		},
		{
			path: "/admin/accounting/bills",
			label: m.nav_bills_expenses(),
			isVisible: canViewAccountingBills,
		},
		{
			path: "/admin/accounting/bills/aci",
			label: m.nav_aci_costs(),
			isVisible: canViewAccountingBills,
		},
		{
			path: "/admin/accounting/orders",
			label: m.nav_orders(),
			isVisible: (userRole) => hasPermission(userRole, "accounting:view"),
		},
		{
			path: "/admin/accounting/cashflow",
			label: m.nav_cash_flow(),
			isVisible: (userRole) => hasPermission(userRole, "accounting:view"),
		},
		{
			path: "/admin/accounting/aging",
			label: m.nav_overdue_aging(),
			isVisible: (userRole) => hasPermission(userRole, "accounting:view"),
		},
		{
			path: "/admin/accounting/plans",
			label: m.nav_payment_plans(),
			isVisible: (userRole) => hasPermission(userRole, "accounting:view"),
		},
		{
			path: "/admin/accounting/points",
			label: m.nav_points(),
			isVisible: (userRole) => hasPermission(userRole, "accounting:view"),
		},
		{
			path: "/admin/accounting/history",
			label: m.nav_payment_history(),
			isVisible: (userRole) => hasPermission(userRole, "accounting:view"),
		},
		{
			path: "/admin/accounting/audit",
			label: m.nav_audit_log(),
			isVisible: (userRole) => hasPermission(userRole, "accounting:view"),
		},
	];
}

function canViewMarketingEmails(userRole?: string, userEmail?: string | null): boolean {
	return canAccessMarketingEmails(userRole, userEmail);
}

function getMarketingItems(): NavItem[] {
	return [
		{
			path: "/marketing/events",
			label: "Events",
			isVisible: (userRole, userEmail) =>
				canAccessInternalEvents(userRole, userEmail),
		},
		{ path: "/marketing/campaigns", label: "Contract savings", isVisible: canViewMarketingEmails },
		{
			path: "/marketing/users",
			label: m.nav_users(),
			isVisible: canViewMarketingEmails,
		},
		{
			path: "/marketing/emails",
			label: m.nav_emails(),
			isVisible: canViewMarketingEmails,
			children: [
				{ path: "/marketing/emails", label: "Activity" },
				{ path: "/marketing/emails/create", label: "Create Campaign" },
			],
		},
	];
}

function canViewMarketingNav(userRole?: string, userEmail?: string | null): boolean {
	return (
		canAccessInternalEvents(userRole, userEmail) ||
		canViewMarketingEmails(userRole, userEmail)
	);
}

function canViewAdminNav(userRole?: string): boolean {
	return hasPermission(userRole, "admin:view");
}

function getAdminItems(): NavItem[] {
	return [
		{
			path: "/admin",
			label: m.nav_dashboard(),
			isVisible: canViewAdminNav,
			children: [
				{ path: "/admin?tab=inventory", label: m.nav_inventory() },
				{ path: "/admin?tab=orders", label: m.nav_orders() },
				{ path: "/admin?tab=contracts", label: m.nav_contracts() },
				{ path: "/admin?tab=users", label: m.nav_users() },
				{ path: "/admin?tab=recovery", label: "Account Recovery" },
				{ path: "/admin?tab=commissions", label: m.nav_commissions() },
			],
		},
		{ path: "/admin/analytics", label: m.nav_analytics(), isVisible: canViewAdminNav },
		{ path: "/admin/leads", label: "Leads", isVisible: canViewAdminNav },
		{ path: "/admin/copilot", label: m.nav_copilot(), isVisible: canViewAdminNav },
		{ path: "/admin/discounts", label: m.nav_discounts(), isVisible: canViewAdminNav },
		{ path: "/admin/knowledgebase", label: m.nav_knowledgebase(), isVisible: canViewAdminNav },
		{ path: "/admin/flow", label: m.nav_flow_explorer(), isVisible: canViewAdminNav },
		{ path: "/admin/crons", label: m.nav_automations(), isVisible: canViewAdminNav },
	];
}

function getNavGroupDefinitions(): NavGroupDefinition[] {
	return [
		{
			id: "roasting",
			label: "Roasting",
			basePath: "/roasting",
			isVisible: canAccessRoasting,
			items: [
				{ path: "/roasting", label: "Roast workspace" },
				{ path: "/roasting/history", label: "Roasts & profiles" },
				{ path: "/roasting/operations", label: "Roastery operations" },
				{ path: "/roasting/planning", label: "Production planner" },
				{ path: "/roasting/yield", label: "Yield & packaging" },
				{ path: "/roasting/coffees", label: "Coffee library" },
				{ path: "/roasting/devices", label: "Device connections" },
			],
		},
		{
			id: "inventory",
			label: m.nav_inventory(),
			basePath: "/inventory",
			items: getInventoryItems(),
			isVisible: (userRole) => hasPermission(userRole, "inventory:view"),
		},
		{
			id: "sales",
			label: m.nav_sales(),
			basePath: "/sales",
			items: getSalesItems(),
			isVisible: (userRole) => hasPermission(userRole, "sales:view"),
		},
		{
			id: "marketing",
			label: "Marketing",
			basePath: "/marketing",
			items: getMarketingItems(),
			isVisible: canViewMarketingNav,
		},
		{
			id: "logistics",
			label: m.nav_logistics(),
			basePath: "/logistics",
			items: getLogisticsItems(),
			isVisible: canViewLogisticsNav,
		},
		{
			id: "support",
			label: m.nav_support(),
			basePath: "/support",
			items: getSupportItems(),
			isVisible: (userRole) => hasPermission(userRole, "internal:access"),
		},
		{
			id: "accounting",
			label: m.nav_accounting(),
			basePath: "/admin/accounting",
			items: getAccountingItems(),
			isVisible: (userRole, userEmail) =>
				canViewAccountingBills(userRole, userEmail),
		},
		{
			id: "admin",
			label: m.nav_admin(),
			basePath: "/admin",
			items: getAdminItems(),
			isVisible: canViewAdminNav,
		},
	];
}

function resolveNavItemsForRole(
	items: NavItem[],
	userRole?: string,
	userEmail?: string | null,
): NavItem[] {
	return items
		.filter((item) =>
			item.isVisible ? item.isVisible(userRole, userEmail) : true,
		)
		.map(({ isVisible: _isVisible, children, ...item }) => ({
			...item,
			...(children
				? {
						children: resolveNavItemsForRole(children, userRole, userEmail),
					}
				: {}),
		}));
}

export interface NavLocationState {
	pathname: string;
	search: string;
}

export function getNavGroupsForRole(
	userRole?: string,
	userEmail?: string | null,
): NavGroup[] {
	return getNavGroupDefinitions()
		.filter((group) => group.isVisible(userRole, userEmail))
		.map(({ isVisible: _isVisible, ...group }) => {
			const items = resolveNavItemsForRole(group.items, userRole, userEmail);
			return {
				...group,
				basePath: items[0]?.path ?? group.basePath,
				items,
			};
		});
}

export function getActiveGroupId(pathname: string): NavGroupId | null {
	if (pathname === "/roasting" || pathname.startsWith("/roasting/")) return "roasting";
	if (pathname.startsWith("/inventory")) return "inventory";
	if (pathname.startsWith("/sales")) return "sales";
	if (pathname.startsWith("/marketing")) return "marketing";
	if (pathname.startsWith("/logistics")) return "logistics";
	if (pathname.startsWith("/support")) return "support";
	if (pathname.startsWith("/security")) return "support";
	if (pathname.startsWith("/admin/accounting")) return "accounting";
	if (pathname.startsWith("/admin")) return "admin";
	return null;
}

export function isNavPathActive(
	path: string,
	locationState: NavLocationState,
): boolean {
	const currentPath = locationState.pathname;
	const currentUrl = locationState.pathname + locationState.search;
	const currentTab = new URLSearchParams(locationState.search).get("tab");

	if (path.includes("?")) {
		return currentUrl === path;
	}

	if (path.split("/").length === 2) {
		if (path === "/admin") {
			return currentPath === path && !currentTab;
		}
		return currentPath === path;
	}

	if (
		path === "/admin/accounting" ||
		path === "/sales/emails" ||
		path === "/marketing/emails"
	) {
		return currentPath === path;
	}

	if (currentPath === path) return true;

	if (currentPath.startsWith(path + "/")) {
		return true;
	}

	return false;
}
